import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { warehousesAPI, uploadAPI } from "../services/api";
import toast from "react-hot-toast";
import {
  ArrowLeft, Plus, Pencil, Trash2, Package, ClipboardCheck,
  Check, X, ChevronDown, ChevronUp, Clock, FileJson, Camera,
} from "lucide-react";
import JsonImportBox from "../components/JsonImportBox";
import Pagination from "../components/Pagination";

// ─── Types ────────────────────────────────────────────────────────────────────
interface InvItem   { _id: string; name: string; unit: string; quantity: number; notes: string; }
interface Unit      { _id: string; roomName: string; notes: string; inventory: InvItem[]; }
interface Warehouse { _id: string; name: string; location: string; notes: string; units: Unit[]; }

interface TicketItem {
  name: string; unit: string; expected: number;
  ok: boolean; missing: number; notes: string;
}
interface Ticket {
  _id: string; roomName: string; createdAt: string;
  items: TicketItem[]; hygieneRating: string; qualityRating: string; overallNotes: string;
  images?: string[];
  inspectedBy?: { _id: string; name: string } | null;
}

const INPUT = "w-full px-3 py-2.5 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400";
const RATING = {
  good: { label: "Tốt", cls: "bg-emerald-100 text-emerald-700" },
  ok:   { label: "Đạt", cls: "bg-yellow-100 text-yellow-700" },
  bad:  { label: "Kém", cls: "bg-red-100 text-red-600" },
};

// ─── Table helpers ────────────────────────────────────────────────────────────
function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`px-4 py-2.5 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wide bg-zinc-50 ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 text-sm text-zinc-700 ${className}`}>{children}</td>;
}

// ─── Room Inventory Table ─────────────────────────────────────────────────────
function RoomInventoryPanel({ warehouseId, unit, onUpdated }: {
  warehouseId: string; unit: Unit; onUpdated: (w: Warehouse) => void;
}) {
  const [adding, setAdding]     = useState(false);
  const [jsonMode, setJsonMode] = useState(false);
  const [editItem, setEditItem] = useState<InvItem | null>(null);
  const [form, setForm]         = useState({ name: "", unit: "", quantity: "1", notes: "" });
  const [saving, setSaving]     = useState(false);
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const openAdd  = () => { setForm({ name: "", unit: "", quantity: "1", notes: "" }); setEditItem(null); setAdding(true); };
  const openEdit = (item: InvItem) => {
    setForm({ name: item.name, unit: item.unit, quantity: String(item.quantity), notes: item.notes || "" });
    setEditItem(item); setAdding(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nhập tên hàng"); return; }
    setSaving(true);
    try {
      const data = { name: form.name, unit: form.unit, quantity: Number(form.quantity) || 1, notes: form.notes };
      const res = editItem
        ? await warehousesAPI.updateInventoryItem(warehouseId, unit._id, editItem._id, data)
        : await warehousesAPI.addInventoryItem(warehouseId, unit._id, data);
      onUpdated(res.data.data);
      setAdding(false);
      toast.success(editItem ? "Đã cập nhật" : "Đã thêm");
    } catch { toast.error("Lỗi lưu"); } finally { setSaving(false); }
  };

  const del = async (item: InvItem) => {
    if (!window.confirm(`Xoá "${item.name}"?`)) return;
    try {
      const res = await warehousesAPI.deleteInventoryItem(warehouseId, unit._id, item._id);
      onUpdated(res.data.data); toast.success("Đã xoá");
    } catch { toast.error("Lỗi xoá"); }
  };

  const paged = unit.inventory.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="space-y-3">
      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-zinc-200">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-zinc-200">
              <Th className="w-8">#</Th>
              <Th>Tên hàng</Th>
              <Th className="w-24 text-center">ĐVT</Th>
              <Th className="w-28 text-center">SL chuẩn</Th>
              <Th>Ghi chú</Th>
              <Th className="w-20 text-center">Thao tác</Th>
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 && !adding ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-zinc-400">
                  Chưa có hàng. Thêm để tạo danh sách chuẩn cho phòng này.
                </td>
              </tr>
            ) : (
              paged.map((item, i) => (
                <tr key={item._id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                  <Td className="text-zinc-400 text-xs">{(page - 1) * pageSize + i + 1}</Td>
                  <Td className="font-medium">{item.name}</Td>
                  <Td className="text-center text-zinc-500">{item.unit || "—"}</Td>
                  <Td className="text-center font-semibold text-emerald-700">{item.quantity}</Td>
                  <Td className="text-zinc-400 text-xs italic">{item.notes || "—"}</Td>
                  <Td className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => openEdit(item)} className="p-1.5 text-zinc-300 hover:text-indigo-600 touch-manipulation"><Pencil size={14} /></button>
                      <button onClick={() => del(item)}      className="p-1.5 text-zinc-300 hover:text-red-500 touch-manipulation"><Trash2 size={14} /></button>
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} total={unit.inventory.length} pageSize={pageSize} onChange={setPage} onPageSizeChange={s => { setPageSize(s); setPage(1); }} />

      {/* Add / JSON form */}
      {jsonMode ? (
        <JsonImportBox
          fields={[
            { key: "name",     label: "Tên đồ vật / hàng hoá trong phòng", required: true },
            { key: "unit",     label: "Đơn vị tính (cái, chai, bộ...)" },
            { key: "quantity", label: "Số lượng chuẩn cần có (mặc định: 1)" },
            { key: "notes",    label: "Ghi chú (loại, kích thước...)" },
          ]}
          example={`[\n  {"name": "Bàn chải đánh răng", "unit": "cái", "quantity": 2},\n  {"name": "Dầu gội", "unit": "chai", "quantity": 1},\n  {"name": "Khăn tắm", "unit": "cái", "quantity": 4, "notes": "loại dày 70x140"}\n]`}
          onImport={items => warehousesAPI.bulkAddInventoryItems(warehouseId, unit._id, items).then(res => onUpdated(res.data.data))}
          onClose={() => setJsonMode(false)}
        />
      ) : adding ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-3 space-y-2">
          <input className={INPUT} placeholder="Tên hàng *" value={form.name} autoFocus
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <div className="grid grid-cols-2 gap-2">
            <input className={INPUT} placeholder="ĐVT (cái, chai...)" value={form.unit}
              onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} />
            <input type="number" inputMode="numeric" min={0} className={INPUT} placeholder="Số lượng" value={form.quantity}
              onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} />
          </div>
          <input className={INPUT} placeholder="Ghi chú (tuỳ chọn)" value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          <div className="flex gap-2">
            <button onClick={save} disabled={saving}
              className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-semibold disabled:opacity-50 touch-manipulation">
              {saving ? "Đang lưu..." : editItem ? "Cập nhật" : "Thêm hàng"}
            </button>
            <button onClick={() => setAdding(false)}
              className="px-4 border border-zinc-200 rounded-xl text-sm text-zinc-600 touch-manipulation">Huỷ</button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <button onClick={openAdd} className="flex items-center gap-1.5 text-sm text-emerald-700 font-medium touch-manipulation">
            <Plus size={14} /> Thêm hàng
          </button>
          <button onClick={() => setJsonMode(true)} className="flex items-center gap-1.5 text-sm text-indigo-500 font-medium touch-manipulation">
            <FileJson size={14} /> Nhập JSON
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Inspection Form ──────────────────────────────────────────────────────────
function InspectionForm({ warehouseId, unit, onDone }: {
  warehouseId: string; unit: Unit; onDone: () => void;
}) {
  const [items, setItems] = useState<TicketItem[]>(() =>
    unit.inventory.map(inv => ({ name: inv.name, unit: inv.unit, expected: inv.quantity, ok: true, missing: 0, notes: inv.notes || "" }))
  );
  const [hygiene, setHygiene]           = useState("");
  const [quality, setQuality]           = useState("");
  const [overallNotes, setOverallNotes] = useState("");
  const [saving, setSaving]             = useState(false);
  const [images, setImages]             = useState<string[]>([]);
  const [uploading, setUploading]       = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const upd = (idx: number, field: keyof TicketItem, val: unknown) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));

  const handleFilesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const roomSlug = unit.roomName.replace(/\s+/g, "_");
        const fileName = `KiemKho_${roomSlug}_${Date.now()}`;
        const res = await uploadAPI.inspection(file, fileName);
        setImages(prev => [...prev, res.data.url]);
      }
    } catch {
      toast.error("Upload ảnh thất bại, thử lại");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (idx: number) => setImages(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await warehousesAPI.createTicket(warehouseId, unit.roomName, {
        items, hygieneRating: hygiene, qualityRating: quality, overallNotes, images,
      });
      toast.success("Đã lưu phiếu kiểm kho");
      onDone();
    } catch { toast.error("Lỗi lưu phiếu"); } finally { setSaving(false); }
  };

  const RatingBtn = ({ val, cur, set }: { val: string; cur: string; set: (v: string) => void }) => {
    const cfg = RATING[val as keyof typeof RATING];
    return (
      <button onClick={() => set(cur === val ? "" : val)}
        className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition-all touch-manipulation
          ${cur === val ? `${cfg.cls} border-current` : "bg-white text-zinc-400 border-zinc-200"}`}>
        {cfg.label}
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b border-zinc-100">
        <button onClick={onDone} className="p-2 -ml-1 text-zinc-400 hover:text-zinc-700 touch-manipulation"><ArrowLeft size={18} /></button>
        <div>
          <p className="text-xs text-zinc-400">Phiếu kiểm kho</p>
          <p className="font-bold text-zinc-900">{unit.roomName}</p>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="text-center py-10 text-zinc-400 text-sm">Phòng chưa có hàng. Thêm danh sách hàng trong phòng trước.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-zinc-200">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-zinc-200">
                <Th>Tên hàng</Th>
                <Th className="w-24 text-center">SL chuẩn</Th>
                <Th className="w-36 text-center">Tình trạng</Th>
                <Th className="w-32 text-center">Thiếu</Th>
                <Th>Ghi chú</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} className={`border-b border-zinc-100 ${!item.ok ? "bg-red-50" : ""}`}>
                  <Td className="font-medium">{item.name}</Td>
                  <Td className="text-center text-zinc-500">{item.expected} {item.unit}</Td>
                  <Td className="text-center">
                    <div className="flex gap-1 justify-center">
                      <button onClick={() => upd(idx, "ok", true)}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold border transition-all touch-manipulation
                          ${item.ok ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-white text-zinc-300 border-zinc-200"}`}>
                        <Check size={11} /> Đủ
                      </button>
                      <button onClick={() => upd(idx, "ok", false)}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold border transition-all touch-manipulation
                          ${!item.ok ? "bg-red-100 text-red-600 border-red-300" : "bg-white text-zinc-300 border-zinc-200"}`}>
                        <X size={11} /> Thiếu
                      </button>
                    </div>
                  </Td>
                  <Td className="text-center">
                    {!item.ok ? (
                      <input type="number" inputMode="numeric" min={0}
                        className="w-16 px-2 py-1 border border-red-200 rounded-lg text-sm text-center bg-white"
                        value={item.missing || ""} placeholder="0"
                        onChange={e => upd(idx, "missing", Number(e.target.value) || 0)} />
                    ) : <span className="text-zinc-300">—</span>}
                  </Td>
                  <Td>
                    <input className="w-full px-2 py-1 border border-zinc-100 rounded-lg text-xs bg-zinc-50 placeholder:text-zinc-300"
                      value={item.notes} onChange={e => upd(idx, "notes", e.target.value)} placeholder="Ghi chú..." />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 bg-zinc-50 rounded-xl border border-zinc-100 p-4">
        <div>
          <p className="text-xs font-semibold text-zinc-500 mb-2">Vệ sinh</p>
          <div className="flex gap-2">{["good","ok","bad"].map(v => <RatingBtn key={v} val={v} cur={hygiene} set={setHygiene} />)}</div>
        </div>
        <div>
          <p className="text-xs font-semibold text-zinc-500 mb-2">Chất lượng</p>
          <div className="flex gap-2">{["good","ok","bad"].map(v => <RatingBtn key={v} val={v} cur={quality} set={setQuality} />)}</div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-zinc-500 mb-1.5">Ghi chú chung</label>
        <textarea className={`${INPUT} resize-none`} rows={2} value={overallNotes}
          onChange={e => setOverallNotes(e.target.value)} placeholder="Nhận xét tổng quan..." />
      </div>

      {/* Hình ảnh kiểm kho — lưu lên Cloudinary giống ảnh CMND */}
      <div>
        <label className="block text-xs font-medium text-zinc-500 mb-1.5">Hình ảnh</label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFilesChange}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl text-sm font-medium hover:bg-emerald-100 disabled:opacity-50 transition-colors touch-manipulation"
        >
          {uploading ? (
            <>
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600" />
              Đang upload...
            </>
          ) : (
            <><Camera size={15} /> Chọn ảnh</>
          )}
        </button>

        {images.length > 0 && (
          <div className="grid grid-cols-4 gap-2 mt-3">
            {images.map((url, i) => (
              <div key={i} className="relative">
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <img src={url} alt={`Kiểm kho ${i}`} className="w-full h-20 rounded-lg border border-zinc-200 object-cover hover:opacity-80" />
                </a>
                <button
                  type="button"
                  onClick={() => removeImage(i)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 text-xs touch-manipulation"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-2">
        <button onClick={onDone} className="py-3 sm:py-2 sm:px-5 border border-zinc-200 rounded-xl text-sm text-zinc-600 touch-manipulation">Huỷ</button>
        <button onClick={handleSubmit} disabled={saving}
          className="flex-1 py-3 sm:py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-bold disabled:opacity-50 touch-manipulation">
          {saving ? "Đang lưu..." : "Lưu phiếu kiểm"}
        </button>
      </div>
    </div>
  );
}

// ─── Edit Ticket Notes Modal ──────────────────────────────────────────────────
function EditTicketModal({ warehouseId, ticket, onClose, onSaved }: {
  warehouseId: string; ticket: Ticket; onClose: () => void; onSaved: (t: Ticket) => void;
}) {
  const [items, setItems]             = useState<TicketItem[]>(() => ticket.items.map(i => ({ ...i })));
  const [overallNotes, setOverallNotes] = useState(ticket.overallNotes);
  const [saving, setSaving]           = useState(false);

  const updNote = (idx: number, val: string) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, notes: val } : it));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await warehousesAPI.updateTicket(warehouseId, ticket._id, { items, overallNotes });
      onSaved(res.data.data);
      toast.success("Đã cập nhật ghi chú");
      onClose();
    } catch { toast.error("Lỗi lưu"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-zinc-900">Sửa ghi chú — {ticket.roomName}</h3>
          <button onClick={onClose}><X size={18} className="text-zinc-400" /></button>
        </div>

        {items.length > 0 && (
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className={`text-sm flex-shrink-0 w-28 truncate ${!it.ok ? "text-red-600 font-medium" : "text-zinc-600"}`}>{it.name}</span>
                <input className="flex-1 px-2 py-1.5 border border-zinc-200 rounded-lg text-xs"
                  value={it.notes} placeholder="Ghi chú..." onChange={e => updNote(i, e.target.value)} />
              </div>
            ))}
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1.5">Ghi chú chung</label>
          <textarea className="w-full px-3 py-2.5 border border-zinc-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400"
            rows={3} value={overallNotes} onChange={e => setOverallNotes(e.target.value)} />
        </div>

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 border border-zinc-200 rounded-xl text-sm text-zinc-600 touch-manipulation">Huỷ</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold disabled:opacity-50 touch-manipulation">
            {saving ? "Đang lưu..." : "Lưu"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Ticket History Table ─────────────────────────────────────────────────────
function TicketTable({ warehouseId, roomName }: { warehouseId: string; roomName: string }) {
  const [tickets, setTickets]         = useState<Ticket[]>([]);
  const [loading, setLoadingT]        = useState(false);
  const [page, setPage]               = useState(1);
  const [pageSize, setPageSize]       = useState(10);
  const [expanded, setExpanded]       = useState<string | null>(null);
  const [editingTicket, setEditingTicket] = useState<Ticket | null>(null);

  useEffect(() => {
    setLoadingT(true);
    warehousesAPI.getTickets(warehouseId, roomName)
      .then(res => setTickets(res.data.data || []))
      .catch(() => toast.error("Lỗi tải phiếu"))
      .finally(() => setLoadingT(false));
  }, [warehouseId, roomName]);

  const del = async (id: string) => {
    if (!window.confirm("Xoá phiếu kiểm này?")) return;
    try { await warehousesAPI.deleteTicket(warehouseId, id); setTickets(prev => prev.filter(t => t._id !== id)); toast.success("Đã xoá"); }
    catch { toast.error("Lỗi xoá"); }
  };

  const paged = tickets.slice((page - 1) * pageSize, page * pageSize);

  if (loading) return <p className="text-xs text-zinc-400 text-center py-6">Đang tải...</p>;

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-xl border border-zinc-200">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-zinc-200">
              <Th>Ngày kiểm</Th>
              <Th>Người kiểm</Th>
              <Th className="text-center">Kết quả</Th>
              <Th className="text-center">Vệ sinh</Th>
              <Th className="text-center">Chất lượng</Th>
              <Th className="w-20 text-center">Chi tiết</Th>
              <Th className="w-16 text-center">Sửa</Th>
              <Th className="w-16 text-center">Xoá</Th>
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-zinc-400">Chưa có phiếu kiểm nào</td></tr>
            ) : paged.map(t => {
              const missing = t.items.filter(i => !i.ok);
              const bad     = missing.length > 0 || t.hygieneRating === "bad" || t.qualityRating === "bad";
              const isOpen  = expanded === t._id;
              return (
                <React.Fragment key={t._id}>
                  <tr className={`border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${bad ? "bg-red-50 hover:bg-red-50" : ""}`}>
                    <Td>
                      {new Date(t.createdAt).toLocaleString("vi-VN", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" })}
                    </Td>
                    <Td className="text-zinc-500">{t.inspectedBy?.name || "—"}</Td>
                    <Td className="text-center">
                      {bad
                        ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full"><X size={10}/> {missing.length} thiếu</span>
                        : <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full"><Check size={10}/> Đủ</span>}
                    </Td>
                    <Td className="text-center">
                      {t.hygieneRating
                        ? <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${RATING[t.hygieneRating as keyof typeof RATING]?.cls}`}>{RATING[t.hygieneRating as keyof typeof RATING]?.label}</span>
                        : <span className="text-zinc-300 text-xs">—</span>}
                    </Td>
                    <Td className="text-center">
                      {t.qualityRating
                        ? <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${RATING[t.qualityRating as keyof typeof RATING]?.cls}`}>{RATING[t.qualityRating as keyof typeof RATING]?.label}</span>
                        : <span className="text-zinc-300 text-xs">—</span>}
                    </Td>
                    <Td className="text-center">
                      <button onClick={() => setExpanded(isOpen ? null : t._id)}
                        className="p-1.5 text-zinc-400 hover:text-zinc-700 touch-manipulation">
                        {isOpen ? <ChevronUp size={15}/> : <ChevronDown size={15}/>}
                      </button>
                    </Td>
                    <Td className="text-center">
                      <button onClick={() => setEditingTicket(t)} className="p-1.5 text-zinc-300 hover:text-indigo-600 touch-manipulation"><Pencil size={14}/></button>
                    </Td>
                    <Td className="text-center">
                      <button onClick={() => del(t._id)} className="p-1.5 text-zinc-300 hover:text-red-500 touch-manipulation"><Trash2 size={14}/></button>
                    </Td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-zinc-100">
                      <td colSpan={8} className="px-6 py-3 bg-zinc-50">
                        <div className="space-y-1">
                          {t.items.map((item, i) => (
                            <div key={i} className={`flex items-center gap-2 text-sm ${!item.ok ? "text-red-600 font-medium" : "text-zinc-500"}`}>
                              {item.ok ? <Check size={12} className="text-emerald-500 flex-shrink-0"/> : <X size={12} className="text-red-500 flex-shrink-0"/>}
                              <span>{item.name} <span className="text-xs text-zinc-400">({item.expected} {item.unit})</span></span>
                              {!item.ok && item.missing > 0 && <span className="text-xs font-bold text-red-500 ml-1">thiếu {item.missing}</span>}
                              {item.notes && <span className="text-xs text-zinc-400 italic">· {item.notes}</span>}
                            </div>
                          ))}
                          {t.overallNotes && <p className="text-xs text-zinc-400 italic pt-2 border-t border-zinc-200 mt-2">{t.overallNotes}</p>}
                          {t.images && t.images.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-200 mt-2">
                              {t.images.map((url, i) => (
                                <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                                  <img src={url} alt={`Ảnh kiểm kho ${i}`} className="w-16 h-16 rounded-lg border border-zinc-200 object-cover hover:opacity-80" />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination page={page} total={tickets.length} pageSize={pageSize} onChange={setPage} onPageSizeChange={s => { setPageSize(s); setPage(1); }} />

      {editingTicket && (
        <EditTicketModal
          warehouseId={warehouseId}
          ticket={editingTicket}
          onClose={() => setEditingTicket(null)}
          onSaved={updated => setTickets(prev => prev.map(x => x._id === updated._id ? updated : x))}
        />
      )}
    </div>
  );
}

// ─── Room Panel ───────────────────────────────────────────────────────────────
function RoomPanel({ warehouseId, unit, onUpdated }: {
  warehouseId: string; unit: Unit; onUpdated: (w: Warehouse) => void;
}) {
  const [mode, setMode]               = useState<"view" | "inspect">("view");
  const [tab, setTab]                 = useState<"inventory" | "tickets">("inventory");

  if (mode === "inspect") {
    return (
      <div className="p-4">
        <InspectionForm warehouseId={warehouseId} unit={unit}
          onDone={() => { setMode("view"); setTab("tickets"); }} />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Tabs + action */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex bg-zinc-100 rounded-xl p-1 gap-1">
          <button onClick={() => setTab("inventory")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors touch-manipulation
              ${tab === "inventory" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-700"}`}>
            <span className="flex items-center gap-1.5"><Package size={12}/> Hàng trong phòng ({unit.inventory.length})</span>
          </button>
          <button onClick={() => setTab("tickets")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors touch-manipulation
              ${tab === "tickets" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-700"}`}>
            <span className="flex items-center gap-1.5"><Clock size={12}/> Lịch sử kiểm</span>
          </button>
        </div>

        {tab === "inventory" && unit.inventory.length > 0 && (
          <button onClick={() => setMode("inspect")}
            className="flex items-center gap-1.5 ml-auto px-4 py-2 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-700 touch-manipulation">
            <ClipboardCheck size={13}/> Tạo phiếu kiểm
          </button>
        )}
      </div>

      {tab === "inventory" && (
        <RoomInventoryPanel warehouseId={warehouseId} unit={unit} onUpdated={onUpdated} />
      )}
      {tab === "tickets" && (
        <TicketTable warehouseId={warehouseId} roomName={unit.roomName} />
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function WarehouseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [warehouse, setWarehouse]       = useState<Warehouse | null>(null);
  const [loading, setLoading]           = useState(true);
  const [expandedUnit, setExpandedUnit] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    warehousesAPI.getAll()
      .then(res => setWarehouse(res.data.data.find((x: Warehouse) => x._id === id) || null))
      .catch(() => toast.error("Lỗi tải kho"))
      .finally(() => setLoading(false));
  }, [id]);

  const handleUpdated = (updated: Warehouse) => setWarehouse(updated);

  if (loading) return <div className="flex items-center justify-center py-20 text-zinc-400"><Package size={24} className="animate-pulse mr-2"/> Đang tải...</div>;
  if (!warehouse) return <div className="text-center py-20 text-zinc-400">Không tìm thấy kho</div>;

  return (
    <div className="space-y-4 pb-10">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/warehouse" className="p-2 -ml-1 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-xl touch-manipulation">
          <ArrowLeft size={20}/>
        </Link>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Package size={18} className="text-emerald-600 flex-shrink-0"/>
            <h1 className="text-xl font-bold text-zinc-900 truncate">{warehouse.name}</h1>
          </div>
          {warehouse.location && <p className="text-xs text-zinc-400 mt-0.5 truncate">{warehouse.location}</p>}
        </div>
      </div>

      {/* Rooms */}
      {warehouse.units.length === 0 ? (
        <div className="text-center py-16 text-zinc-400">
          <Package size={36} className="mx-auto mb-3 opacity-30"/>
          <p className="text-sm">Kho chưa có căn phòng nào</p>
          <Link to="/warehouse" className="text-sm text-emerald-600 hover:underline mt-2 block">← Quay lại để thêm căn</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {warehouse.units.map(unit => {
            const isOpen = expandedUnit === unit._id;
            return (
              <div key={unit._id} className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
                <button
                  className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-zinc-50 active:bg-zinc-100 transition-colors touch-manipulation"
                  onClick={() => setExpandedUnit(isOpen ? null : unit._id)}
                >
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <Package size={18} className="text-emerald-600"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-zinc-900 text-base leading-tight truncate">{unit.roomName}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">
                      {unit.inventory.length} loại hàng{unit.notes ? ` · ${unit.notes}` : ""}
                    </p>
                  </div>
                  <ClipboardCheck size={16} className="text-zinc-300 flex-shrink-0"/>
                  {isOpen ? <ChevronUp size={18} className="text-zinc-400 flex-shrink-0"/> : <ChevronDown size={18} className="text-zinc-400 flex-shrink-0"/>}
                </button>
                {isOpen && (
                  <div className="border-t border-zinc-100">
                    <RoomPanel warehouseId={warehouse._id} unit={unit} onUpdated={handleUpdated}/>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
